import { useCallback } from 'react';
import { pick } from 'js/utils/ramda.util';
import { getTimeZone } from 'js/utils/date.util';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useGetErrorMessage } from 'js/hooks/server/hooks';
import { useShowNotification } from 'js/components/common/Notifications';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as server from './server';
import * as actions from '../store/actions';
import { useDynamicReducerDispatch, useDynamicReducerSelector } from '../store';

/**
 * @param props {Object}
 * @param props.type {"revoke"|"add"}
 * @param [props.id] {String} - mandatory if type = "revoke"
 */
export const useExecuteAction = (props) => {
    const { type, id } = props;
    const isMounted = useIsMounted();
    const translate = useComponentTranslations();
    const dispatch = useDynamicReducerDispatch();
    const getErrorMessage = useGetErrorMessage();
    const showNotification = useShowNotification({ iconType: 'info' });
    const { scheduledBreaks, revision, isActionsDisabled } = useDynamicReducerSelector(pick([
        'scheduledBreaks', 'revision', 'isActionsDisabled'
    ]));

    return useCallback(async ({ start, end }) => {
        if (!isMounted() || isActionsDisabled) {
            return;
        }

        let timeSlotExclusions = scheduledBreaks.map(item => ({ id: item.id }));

        switch (type) {
            case 'revoke': {
                timeSlotExclusions = timeSlotExclusions.filter(item => item.id !== id);
                break;
            }
            case 'add': {
                const newScheduledBreak = { start, end, timeZone: getTimeZone() };

                timeSlotExclusions = [...timeSlotExclusions, newScheduledBreak];
                break;
            }
            default: break;
        }

        dispatch(actions.setError({ error: '' }));
        dispatch(actions.toggleIsActionsDisabled(true));

        try {
            await server.setTimeSlotExclusions({ revision, timeSlotExclusions });

            showNotification({ message: translate(`${type}.success`) });
            isMounted() && dispatch(actions.toggleIsRefetchNeeded(true));
        } catch (error) {
            const errorMsg = getErrorMessage(error);

            isMounted() && dispatch(actions.setError({ error: errorMsg, isFetchError: false }));
        } finally {
            isMounted() && dispatch(actions.toggleIsActionsDisabled(false));
        }
    }, [
        id, scheduledBreaks, type, isActionsDisabled, dispatch,
        isMounted, translate, revision, showNotification, getErrorMessage
    ]);
};
